import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { motion, type Variants } from 'motion/react'
import {
  submitCheckin,
  toNullable,
  todayLocalISO,
  type DailyTrackingPayload,
  type PainLogPayload,
} from '@pelvia/shared/lib/patient/checkin'
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder'
import { useCheckinPrefill } from '@pelvia/shared/hooks/patient/useCheckinPrefill'
import { useRecentSymptoms } from '@pelvia/shared/hooks/patient/useRecentSymptoms'
import { useMedications } from '@pelvia/shared/hooks/patient/useMedications'
import { useDashboard } from '@pelvia/shared/hooks/patient/useDashboard'
import { api } from '@/lib/api'
import { captureEvent } from '@/lib/analytics'
import PrefillPill from '@/components/PrefillPill'
import type { PatientOutletContext } from './PatientLayout'

// Chip option definitions — single source of truth for the flow. Values must
// match the legacy backend enum expectations (see static/patient.html chip data-value).
const PAIN_CHARACTERS = [
  ['sharp', 'Sharp'],
  ['burning', 'Burning'],
  ['aching', 'Aching'],
  ['cramping', 'Cramping'],
  ['stabbing', 'Stabbing'],
  ['throbbing', 'Throbbing'],
  ['pressure', 'Pressure'],
  ['dull', 'Dull'],
] as const

const PAIN_LOCATIONS = [
  ['lower_abdomen', 'Lower Abdomen'],
  ['pelvic', 'Pelvic'],
  ['lower_back', 'Lower Back'],
  ['hip', 'Hip'],
  ['leg', 'Leg'],
  ['bladder', 'Bladder'],
  ['rectal', 'Rectal'],
] as const

const CYCLE_OPTIONS = [
  ['bleeding', 'Bleeding'],
  ['no_bleeding', 'No bleeding'],
] as const

// Cycle chips that imply some bleeding is happening — the pad/tampon counters
// only appear for these (no point asking about products on a non-bleeding
// day). Kept in sync with FLOW_VALUES in data/db.py.
const BLEEDING_CYCLE_VALUES = new Set(['bleeding'])

const MUCUS_OPTIONS = [
  ['dry', 'Dry'],
  ['sticky', 'Sticky'],
  ['creamy', 'Creamy'],
  ['watery', 'Watery'],
  ['egg_white', 'Egg white'],
  ['unsure', 'Unsure'],
] as const

const CORE_SYMPTOMS = [
  ['pelvic_pain', 'Pelvic pain'],
  ['fatigue', 'Fatigue'],
  ['urgency', 'Urinary urgency'],
  ['bloating', 'Bloating'],
  ['mood', 'Mood changes'],
  ['backpain', 'Back pain'],
  ['dyspareunia', 'Pain with intercourse'],
] as const

const EXPANDED_SYMPTOM_GROUPS: Array<{ label: string; options: ReadonlyArray<readonly [string, string]> }> = [
  {
    label: 'Pain',
    options: [
      ['lower_abdominal_pain', 'Lower abdominal pain'],
      ['hip_pain', 'Hip pain'],
      ['leg_pain', 'Leg pain'],
      ['tailbone_pain', 'Tailbone pain'],
      ['shoulder_pain', 'Shoulder pain'],
      ['rectal_pain', 'Rectal pain'],
      ['vaginal_pain', 'Vaginal pain'],
    ],
  },
  {
    label: 'Bowel & Bladder',
    options: [
      ['constipation', 'Constipation'],
      ['diarrhea', 'Diarrhea'],
      ['painful_bowel', 'Painful bowel movements'],
      ['blood_in_stool', 'Blood in stool'],
      ['urinary_frequency', 'Urinary frequency'],
      ['painful_urination', 'Painful urination'],
      ['bladder_pressure', 'Bladder pressure'],
      ['blood_in_urine', 'Blood in urine'],
    ],
  },
  {
    label: 'Energy & Cognitive',
    options: [
      ['low_energy', 'Low energy'],
      ['brain_fog', 'Brain fog'],
      ['dizziness', 'Dizziness'],
      ['lightheadedness', 'Lightheadedness'],
      ['passing_out', 'Passing out / fainting'],
      ['pica', 'Pica (craving ice/non-food)'],
      ['insomnia', 'Insomnia'],
      ['appetite_changes', 'Appetite changes'],
    ],
  },
  {
    label: 'Mood & Hormonal',
    options: [
      ['anxiety', 'Anxiety'],
      ['depression', 'Depression'],
      ['irritability', 'Irritability'],
      ['breast_tenderness', 'Breast tenderness'],
      ['hot_flashes', 'Hot flashes'],
      ['night_sweats', 'Night sweats'],
    ],
  },
  {
    label: 'Systemic',
    options: [
      ['nausea', 'Nausea'],
      ['vomiting', 'Vomiting'],
      ['fever', 'Fever'],
      ['chills', 'Chills'],
      ['headache', 'Headache'],
    ],
  },
  {
    label: 'Bleeding & Discharge',
    options: [
      ['spotting_between', 'Spotting between periods'],
      ['unusual_discharge', 'Unusual discharge'],
      ['post_intercourse_bleeding', 'Post-intercourse bleeding'],
    ],
  },
  {
    // Ovulation signals — feed the cycle predictor's symptom-based ovulation
    // detection (OPK > mittelschmerz > breast tenderness > libido).
    label: 'Fertility signals',
    options: [
      ['opk_positive', 'Positive ovulation test (OPK)'],
      ['mittelschmerz', 'Mid-cycle ovulation pain'],
      ['breast_tenderness', 'Breast tenderness'],
      ['libido_spike', 'Higher libido'],
    ],
  },
  {
    label: 'Musculoskeletal',
    options: [
      ['hip_tightness', 'Hip tightness'],
      ['pelvic_floor_tension', 'Pelvic floor tension'],
      ['leg_heaviness', 'Leg heaviness'],
      ['joint_pain', 'Joint pain'],
    ],
  },
  {
    label: 'Skin',
    options: [
      ['cystic_acne', 'Cystic acne'],
      ['hormonal_acne', 'Hormonal acne'],
      ['skin_breakout', 'Skin breakout'],
      ['oily_skin', 'Oily skin'],
    ],
  },
]

// All symptom groups for the expanded catalog — the seven core symptoms lead as
// a "Common" group, followed by the detailed categories.
const ALL_SYMPTOM_GROUPS: Array<{ label: string; options: ReadonlyArray<readonly [string, string]> }> = [
  { label: 'Common', options: CORE_SYMPTOMS },
  ...EXPANDED_SYMPTOM_GROUPS,
]

// Symptom id → display label across the whole catalog, so a carried-forward
// symptom from any category can be rendered with its proper name.
const SYMPTOM_LABELS: Record<string, string> = Object.fromEntries([
  ...CORE_SYMPTOMS,
  ...EXPANDED_SYMPTOM_GROUPS.flatMap((g) => g.options),
])

function humanizeSymptom(id: string): string {
  const s = id.replace(/_/g, ' ')
  return s.charAt(0).toUpperCase() + s.slice(1)
}

const STRESS_OPTIONS = [
  ['1', 'None'],
  ['2', 'Mild'],
  ['3', 'Moderate'],
  ['4', 'High'],
  ['5', 'Extreme'],
  ['unsure', 'Unsure'],
] as const

const FOOD_OPTIONS = [
  ['anti_inflammatory', 'Anti-inflammatory'],
  ['fried_food', 'Fried food'],
  ['processed', 'Processed food'],
  ['dairy', 'Dairy'],
  ['gluten', 'Gluten'],
  ['sugar', 'High sugar'],
  ['caffeine', 'Caffeine'],
  ['spicy', 'Spicy food'],
  ['high_fiber', 'High fiber'],
] as const

const ALCOHOL_OPTIONS = [
  ['none', 'None'],
  ['light', 'Light (1–2)'],
  ['moderate', 'Moderate (3–4)'],
  ['heavy', 'Heavy (5+)'],
  ['unsure', 'Unsure'],
] as const

// The medications step (6) is only rendered when the user has scheduled
// (non-as-needed) active meds; otherwise the flow ends at step 5. TOTAL_STEPS
// is computed per-render inside the component.
const DRAFT_KEY = 'pelvia_checkin_draft'

interface DraftState {
  date: string
  step: number
  pain_score: number | null
  pain_character: string[]
  pain_locations: string[]
  notes: string
  sleep_hours: number
  energy_level: number
  cycle_phase: string | null
  cervical_mucus: string | null
  pads_count: number | null
  tampons_count: number | null
  symptoms: string[]
  other_symptoms: string[]
  stress: string | null
  food_tags: string[]
  alcohol: string | null
  expanded: boolean
  // Which meds the user checked off as taken yesterday. Keys are medication_id
  // as string; true = taken. A med absent or false is logged as not taken.
  meds_yesterday: Record<string, boolean>
}

function defaultDraft(): DraftState {
  return {
    date: todayLocalISO(),
    step: 1,
    pain_score: null,
    pain_character: [],
    pain_locations: [],
    notes: '',
    sleep_hours: 7,
    energy_level: 3,
    cycle_phase: null,
    cervical_mucus: null,
    pads_count: null,
    tampons_count: null,
    symptoms: [],
    other_symptoms: [],
    stress: null,
    food_tags: [],
    alcohol: null,
    expanded: false,
    meds_yesterday: {},
  }
}

function yesterdayLocalISO(): string {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toLocaleDateString('en-CA')
}

function loadDraft(): { draft: DraftState; fromStorage: boolean } {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (!raw) return { draft: defaultDraft(), fromStorage: false }
    const parsed = JSON.parse(raw) as Partial<DraftState>
    if (parsed.date !== todayLocalISO()) {
      localStorage.removeItem(DRAFT_KEY)
      return { draft: defaultDraft(), fromStorage: false }
    }
    return { draft: { ...defaultDraft(), ...parsed } as DraftState, fromStorage: true }
  } catch {
    return { draft: defaultDraft(), fromStorage: false }
  }
}

function clampSleep(n: number): number {
  if (!Number.isFinite(n)) return 7
  return Math.max(2, Math.min(12, n))
}

function clampEnergy(n: number): number {
  if (!Number.isFinite(n)) return 3
  return Math.max(1, Math.min(5, Math.round(n)))
}

export default function CheckinPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showToast } = useOutletContext<PatientOutletContext>()

  // Initial draft + whether it was restored from localStorage (in-progress
  // typing the user shouldn't lose). If it wasn't, we're allowed to hydrate
  // from the server below.
  const initialLoad = useRef(loadDraft())
  const [draft, setDraft] = useState<DraftState>(initialLoad.current.draft)
  const draftWasFromStorage = initialLoad.current.fromStorage
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [painMissing, setPainMissing] = useState(false)
  const [prefilledFields, setPrefilledFields] = useState<Set<string>>(() => new Set())
  const [prefillSource, setPrefillSource] = useState<string | null>(null)
  const prefillAppliedRef = useRef(false)
  // Tracks whether the one-shot "hydrate from existing daily_tracking + pain_log"
  // effect has already run. Without this, the dashboard query refetching mid-flow
  // would re-stomp the user's edits.
  const existingHydratedRef = useRef(false)
  const mountedRef = useRef(true)

  const dashboardQuery = useDashboard()
  const prefillQuery = useCheckinPrefill(draft.date)
  const recentSymptomsQuery = useRecentSymptoms()
  const medsQuery = useMedications(true)
  // Only scheduled meds have daily adherence; as-needed meds are logged when taken.
  const scheduledMeds = useMemo(
    () => (medsQuery.data?.medications ?? []).filter((m) => !m.as_needed),
    [medsQuery.data?.medications],
  )
  const medsStepEnabled = scheduledMeds.length > 0
  const TOTAL_STEPS = medsStepEnabled ? 6 : 5

  // Carry-forward: the collapsed symptom list shows what the user has been
  // logging lately. New users (no history) fall back to the core symptoms.
  const quickSymptoms = useMemo<ReadonlyArray<readonly [string, string]>>(() => {
    const recent = (recentSymptomsQuery.data?.symptoms ?? []).filter(
      (id) => !id.startsWith('other'),
    )
    const opts = recent.map((id) => [id, SYMPTOM_LABELS[id] ?? humanizeSymptom(id)] as const)
    return opts.length > 0 ? opts : CORE_SYMPTOMS
  }, [recentSymptomsQuery.data])

  // One-shot hydration from the user's already-saved daily_tracking + pain_log
  // for today. Without this, clicking "Edit" on the dashboard's done-card
  // opened a fresh form and submitting wiped the user's prior values
  // (upsert overwrites every column). Skipped when localStorage already has
  // in-progress typing — that path takes priority. Skipped after first apply
  // so dashboard refetches don't stomp later edits.
  useEffect(() => {
    if (existingHydratedRef.current) return
    if (draftWasFromStorage) {
      existingHydratedRef.current = true
      return
    }
    const dash = dashboardQuery.data
    if (!dash) return
    const todayPain = dash.today_pain
    const todayTracking = dash.today_tracking
    if (!todayPain && !todayTracking) {
      existingHydratedRef.current = true
      return
    }
    existingHydratedRef.current = true
    setDraft((d) => ({
      ...d,
      pain_score: todayPain?.pain_score ?? d.pain_score,
      pain_character: (todayPain?.pain_character as string[] | undefined) ?? d.pain_character,
      pain_locations: (todayPain?.pain_locations as string[] | undefined) ?? d.pain_locations,
      notes: todayPain?.notes ?? d.notes,
      sleep_hours: todayTracking?.sleep_hours != null
        ? clampSleep(todayTracking.sleep_hours)
        : d.sleep_hours,
      energy_level: todayTracking?.energy_level != null
        ? clampEnergy(todayTracking.energy_level)
        : d.energy_level,
      cycle_phase: todayTracking?.cycle_phase ?? d.cycle_phase,
      cervical_mucus: todayTracking?.cervical_mucus ?? d.cervical_mucus,
      pads_count: todayTracking?.pads_count ?? d.pads_count,
      tampons_count: todayTracking?.tampons_count ?? d.tampons_count,
      symptoms: (todayTracking?.symptoms as string[] | undefined) ?? d.symptoms,
      stress: todayTracking?.stress_level != null
        ? String(todayTracking.stress_level)
        : d.stress,
      food_tags: (todayTracking?.food_tags as string[] | undefined) ?? d.food_tags,
      alcohol: todayTracking?.alcohol ?? d.alcohol,
    }))
  }, [dashboardQuery.data, draftWasFromStorage])

  useEffect(() => {
    if (prefillAppliedRef.current) return
    const data = prefillQuery.data
    if (!data || !data.source) return
    prefillAppliedRef.current = true
    setPrefillSource(data.source)
    setDraft((d) => {
      const next = { ...d }
      const filled = new Set<string>()
      // Only overwrite if value is still at default (user hasn't edited yet).
      if (data.sleep_hours != null && d.sleep_hours === 7) {
        next.sleep_hours = clampSleep(data.sleep_hours)
        filled.add('sleep_hours')
      }
      if (data.energy_level != null && d.energy_level === 3) {
        next.energy_level = clampEnergy(data.energy_level)
        filled.add('energy_level')
      }
      if (data.stress_level != null && d.stress == null) {
        next.stress = String(data.stress_level)
        filled.add('stress')
      }
      setPrefilledFields(filled)
      return next
    })
  }, [prefillQuery.data])

  const clearPrefillFor = useCallback((field: string) => {
    setPrefilledFields((prev) => {
      if (!prev.has(field)) return prev
      const next = new Set(prev)
      next.delete(field)
      return next
    })
  }, [])

  useEffect(() => () => { mountedRef.current = false }, [])

  // Persist draft on every state change. Cheap: localStorage writes are sync + small.
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
    } catch {
      // quota / private browsing — non-fatal
    }
  }, [draft])

  // Step 3 (sleep & energy) is hidden when a wearable provided both values —
  // we already have the data; asking again is friction.
  const skipSleepEnergy = !!(
    prefillQuery.data?.source &&
    prefillQuery.data.sleep_hours != null &&
    prefillQuery.data.energy_level != null
  )

  // Self-correct invalid step combinations (e.g. resumed draft at step=2 with pain=0,
  // or step=3 when wearable already provided sleep & energy).
  useEffect(() => {
    if (draft.step === 2 && draft.pain_score === 0) {
      setDraft((d) => ({ ...d, step: 3 }))
    }
  }, [draft.step, draft.pain_score])

  useEffect(() => {
    if (draft.step === 3 && skipSleepEnergy) {
      setDraft((d) => ({ ...d, step: 4 }))
    }
  }, [draft.step, skipSleepEnergy])

  const patch = useCallback((updates: Partial<DraftState>) => {
    setDraft((prev) => ({ ...prev, ...updates }))
  }, [])

  const toggleInArray = useCallback((key: keyof DraftState, value: string) => {
    setDraft((prev) => {
      const current = prev[key] as string[]
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value]
      return { ...prev, [key]: next }
    })
  }, [])

  // Step 2 (pain details) is hidden when no pain was logged — no point asking
  // about the *character* of pain that isn't there.
  const skipPainDetails = draft.pain_score == null || draft.pain_score === 0

  const goNext = useCallback(() => {
    if (draft.step === 1) {
      if (draft.pain_score == null) {
        setPainMissing(true)
        return
      }
      setPainMissing(false)
    }
    if (draft.step < TOTAL_STEPS) {
      let next = draft.step + 1
      if (next === 2 && skipPainDetails) next = 3
      if (next === 3 && skipSleepEnergy) next = 4
      patch({ step: next })
    }
  }, [draft.step, draft.pain_score, skipPainDetails, skipSleepEnergy, patch])

  const goBack = useCallback(() => {
    if (draft.step > 1) {
      let prev = draft.step - 1
      if (prev === 3 && skipSleepEnergy) prev = 2
      if (prev === 2 && skipPainDetails) prev = 1
      patch({ step: prev })
    }
  }, [draft.step, skipPainDetails, skipSleepEnergy, patch])

  const onSubmit = useCallback(async () => {
    if (submitting) return
    if (draft.pain_score == null) {
      setPainMissing(true)
      patch({ step: 1 })
      return
    }

    setSubmitting(true)
    setSubmitError(null)

    const log_date = todayLocalISO()

    const pain: PainLogPayload = {
      pain_score: draft.pain_score,
      pain_character: draft.pain_character,
      pain_locations: draft.pain_locations,
      notes: draft.notes.trim(),
      log_date,
    }

    const symptoms = [...draft.symptoms]
    if (draft.symptoms.includes('other')) {
      for (const entry of draft.other_symptoms) {
        const t = entry.trim()
        if (t) symptoms.push(`other:${t}`)
      }
    }

    const stressRaw = draft.stress
    const stressLevel = stressRaw && stressRaw !== 'unsure' ? parseInt(stressRaw, 10) : null

    // Pad/tampon counts are only meaningful on bleeding days — drop them for
    // anything else so we don't store a stale "0" on a no-bleeding day.
    // flow_products_count is kept as their sum for legacy readers.
    const isBleeding =
      !!draft.cycle_phase && BLEEDING_CYCLE_VALUES.has(draft.cycle_phase)
    const padsForServer = isBleeding ? (draft.pads_count ?? null) : null
    const tamponsForServer = isBleeding ? (draft.tampons_count ?? null) : null
    const flowProductsForServer =
      isBleeding && (padsForServer != null || tamponsForServer != null)
        ? (padsForServer ?? 0) + (tamponsForServer ?? 0)
        : null

    const tracking: DailyTrackingPayload = {
      sleep_hours: clampSleep(draft.sleep_hours),
      energy_level: clampEnergy(draft.energy_level),
      cycle_phase: toNullable(draft.cycle_phase),
      symptoms,
      stress_level: Number.isFinite(stressLevel as number) ? stressLevel : null,
      food_tags: draft.food_tags,
      alcohol: toNullable(draft.alcohol),
      cervical_mucus: toNullable(draft.cervical_mucus),
      pads_count: padsForServer,
      tampons_count: tamponsForServer,
      flow_products_count: flowProductsForServer,
      log_date,
    }

    try {
      await submitCheckin(pain, tracking)
      // Log yesterday's med adherence alongside the check-in. Every scheduled
      // med on the checklist gets a record — checked = taken, unchecked = not
      // taken — so a blank is an explicit "not taken", never a missing answer.
      // Fire-and-forget per med: the endpoint is idempotent (ON CONFLICT DO
      // UPDATE) so a partial failure still leaves the other meds logged, and we
      // never block check-in success on med logging.
      if (medsStepEnabled) {
        const yesterday = yesterdayLocalISO()
        await Promise.allSettled(
          scheduledMeds.map((m) =>
            api.post('/api/patient/medications/adherence', {
              medication_id: m.id,
              log_date: yesterday,
              taken: draft.meds_yesterday[String(m.id)] === true,
            }),
          ),
        )
        queryClient.invalidateQueries({ queryKey: ['patient', 'calendar'] })
      }
      if (!mountedRef.current) return
      localStorage.removeItem(DRAFT_KEY)
      // Refresh dashboard so the streak pill + week chart reflect the new entry.
      await queryClient.invalidateQueries({ queryKey: ['patient', 'dashboard'] })
      // Refresh cycle predictor: a new flow/mucus log can move the predicted
      // next-period date or current-cycle ovulation peak — without this the
      // card stays stale until the 10-min React Query staleTime expires.
      queryClient.invalidateQueries({ queryKey: ['patient', 'cycle-predictor'] })
      captureEvent('daily_log_saved', { pain_score: draft.pain_score })
      showToast('Check-in saved!', 'success')
      navigate('/patient')
    } catch (err) {
      if (!mountedRef.current) return
      const msg = err instanceof Error ? err.message : 'Could not save your check-in. Please try again.'
      setSubmitError(msg)
    } finally {
      if (mountedRef.current) setSubmitting(false)
    }
  }, [submitting, draft, patch, queryClient, showToast, navigate, medsStepEnabled, scheduledMeds])

  const progressPct = useMemo(() => (draft.step / TOTAL_STEPS) * 100, [draft.step])
  const isLastStep = draft.step === TOTAL_STEPS

  return (
    <div className="checkin-page">
      <div className="checkin-header-bar">
        <button
          type="button"
          className="checkin-cancel-btn"
          onClick={() => navigate('/patient')}
          aria-label="Close check-in"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="checkin-progress">
          <div className="checkin-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <span className="checkin-step-label">Step {draft.step} of {TOTAL_STEPS}</span>
      </div>

      <div className="checkin-body">
        {draft.step === 1 && (
          <Step1Pain
            value={draft.pain_score}
            missing={painMissing}
            onChange={(v) => {
              patch({ pain_score: v })
              setPainMissing(false)
            }}
          />
        )}
        {draft.step === 2 && !skipPainDetails && (
          <Step2PainDetails
            character={draft.pain_character}
            locations={draft.pain_locations}
            notes={draft.notes}
            onToggleCharacter={(v) => toggleInArray('pain_character', v)}
            onToggleLocation={(v) => toggleInArray('pain_locations', v)}
            onNotesChange={(notes) => patch({ notes })}
          />
        )}
        {draft.step === 3 && !skipSleepEnergy && (
          <Step3SleepEnergy
            sleepHours={draft.sleep_hours}
            energyLevel={draft.energy_level}
            sleepPrefilled={prefilledFields.has('sleep_hours')}
            energyPrefilled={prefilledFields.has('energy_level')}
            prefillSource={prefillSource}
            onSleepChange={(v) => { clearPrefillFor('sleep_hours'); patch({ sleep_hours: clampSleep(v) }) }}
            onEnergyChange={(v) => { clearPrefillFor('energy_level'); patch({ energy_level: clampEnergy(v) }) }}
          />
        )}
        {draft.step === 4 && (
          <Step4CycleSymptoms
            cyclePhase={draft.cycle_phase}
            mucus={draft.cervical_mucus}
            padsCount={draft.pads_count}
            tamponsCount={draft.tampons_count}
            symptoms={draft.symptoms}
            quickSymptoms={quickSymptoms}
            otherSymptoms={draft.other_symptoms}
            expanded={draft.expanded}
            onCycleChange={(v) => {
              const next: Partial<DraftState> = { cycle_phase: v }
              // Clear the product counts when switching to a non-bleeding cycle
              // so they don't stick around as phantom values.
              if (!BLEEDING_CYCLE_VALUES.has(v)) {
                next.pads_count = null
                next.tampons_count = null
              }
              patch(next)
            }}
            onMucusChange={(v) => patch({ cervical_mucus: v })}
            onPadsChange={(n) => patch({ pads_count: n })}
            onTamponsChange={(n) => patch({ tampons_count: n })}
            onSymptomToggle={(v) => toggleInArray('symptoms', v)}
            onOtherChange={(next) => patch({ other_symptoms: next })}
            onToggleExpanded={() => patch({ expanded: !draft.expanded })}
          />
        )}
        {draft.step === 5 && (
          <Step5Lifestyle
            stress={draft.stress}
            food={draft.food_tags}
            alcohol={draft.alcohol}
            stressPrefilled={prefilledFields.has('stress')}
            prefillSource={prefillSource}
            onStressChange={(v) => { clearPrefillFor('stress'); patch({ stress: v }) }}
            onFoodToggle={(v) => toggleInArray('food_tags', v)}
            onAlcoholChange={(v) => patch({ alcohol: v })}
          />
        )}
        {draft.step === 6 && medsStepEnabled && (
          <Step6Medications
            meds={scheduledMeds}
            taken={draft.meds_yesterday}
            onToggle={(id) =>
              patch({
                meds_yesterday: {
                  ...draft.meds_yesterday,
                  [String(id)]: !draft.meds_yesterday[String(id)],
                },
              })
            }
          />
        )}

        {submitError && (
          <div className="checkin-error" role="alert">{submitError}</div>
        )}
      </div>

      <div className="checkin-footer">
        {draft.step > 1 ? (
          <button type="button" className="btn btn-secondary" onClick={goBack} disabled={submitting}>
            Back
          </button>
        ) : <span />}
        <button
          type="button"
          className="btn btn-primary"
          onClick={isLastStep ? onSubmit : goNext}
          disabled={submitting}
        >
          {submitting ? 'Saving…' : isLastStep ? 'Save check-in' : 'Next'}
        </button>
      </div>
    </div>
  )
}

// -------- Step components --------

const MANKOSKI_LABELS = [
  'No pain',
  'Very mild',
  'Minor',
  'Noticeable',
  'Can be ignored with effort',
  "Can't ignore >30 min",
  'Affects daily tasks',
  'Interferes with sleep',
  'Intense',
  'Excruciating',
  'Unbearable',
]

function Step1Pain({
  value,
  missing,
  onChange,
}: {
  value: number | null
  missing: boolean
  onChange: (n: number) => void
}) {
  return (
    <div className="checkin-step-content">
      <h3 className="checkin-title">How's your pain right now?</h3>
      <p className="checkin-desc">Tap a number to log today's pain</p>
      <div className="pain-scale">
        {Array.from({ length: 11 }, (_, i) => (
          <button
            key={i}
            type="button"
            className={`pain-btn${value === i ? ' selected' : ''}`}
            data-score={i}
            onClick={() => onChange(i)}
          >
            {i}
          </button>
        ))}
      </div>
      <div className="pain-scale-labels">
        <span className="pain-label">No pain</span>
        <span className="pain-label">Worst pain</span>
      </div>
      <div
        className="mankoski-caption"
        aria-live="polite"
        data-empty={value == null ? 'true' : 'false'}
      >
        {value != null ? MANKOSKI_LABELS[value] : 'Mankoski scale — tap a number above'}
      </div>
      {missing && (
        <div className="checkin-error" role="alert">Please select a pain score to continue.</div>
      )}
    </div>
  )
}

function Step2PainDetails({
  character,
  locations,
  notes,
  onToggleCharacter,
  onToggleLocation,
  onNotesChange,
}: {
  character: string[]
  locations: string[]
  notes: string
  onToggleCharacter: (v: string) => void
  onToggleLocation: (v: string) => void
  onNotesChange: (v: string) => void
}) {
  const notesRef = useRef(notes)
  notesRef.current = notes
  const voice = useVoiceRecorder(
    useCallback(
      (text) => {
        const existing = notesRef.current
        onNotesChange(existing ? `${existing} ${text}` : text)
      },
      [onNotesChange],
    ),
  )

  return (
    <div className="checkin-step-content">
      <h3 className="checkin-title">Describe your pain</h3>
      <p className="checkin-desc">Help us understand your pain better</p>
      <label className="form-label">Character</label>
      <ChipGroup options={PAIN_CHARACTERS} selected={character} onToggle={onToggleCharacter} multi />
      <label className="form-label" style={{ marginTop: '0.75rem' }}>Location</label>
      <ChipGroup options={PAIN_LOCATIONS} selected={locations} onToggle={onToggleLocation} multi />
      <label className="form-label" style={{ marginTop: '0.75rem' }}>
        Notes <span className="form-hint">(observations &amp; triggers — type or dictate)</span>
      </label>
      <div className="notes-textarea-wrap">
        <textarea
          className="form-textarea"
          value={notes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="How are you feeling? Any triggers or observations..."
          rows={3}
        />
        {voice.supported && (
          <button
            type="button"
            className={`voice-btn-inline${voice.isRecording ? ' recording' : ''}`}
            onClick={voice.toggle}
            disabled={voice.isTranscribing}
            aria-label={voice.isRecording ? 'Stop recording' : 'Record voice note'}
            aria-pressed={voice.isRecording}
            title={voice.isTranscribing ? 'Transcribing…' : voice.isRecording ? 'Stop recording' : 'Record voice note'}
          >
            {voice.isTranscribing ? (
              <span className="loading-spinner" aria-hidden />
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            )}
          </button>
        )}
      </div>
      {voice.error && (
        <div className="checkin-error" role="alert" style={{ marginTop: '0.5rem' }}>
          {voice.error}
        </div>
      )}
    </div>
  )
}

// Energy scale — 5 labeled options, matching the pain-scale interaction
// pattern so users only learn one thing. Icons are hand-drawn SVG so we can
// animate *parts* of them (eyes droop, mouth widens into a grin, flame
// flickers) rather than scaling a flat emoji. Parent button drives the state
// via Framer Motion's variant propagation: whileHover / animate on the
// button flows down to every motion.* child inside EnergyIcon.

const ICON_SIZE = 34
const FACE_YELLOW = '#FFD43E'
const FACE_STROKE = '#D9A609'
const FACE_FEATURE = '#3b2d1a'

const Z_FLOAT_A: Variants = {
  idle: { y: 0, opacity: 0.8 },
  active: {
    y: [0, -4, 0],
    opacity: [0.8, 1, 0.8],
    transition: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' },
  },
}
const Z_FLOAT_B: Variants = {
  idle: { y: 0, opacity: 0.55 },
  active: {
    y: [0, -3, 0],
    opacity: [0.55, 0.9, 0.55],
    transition: { duration: 1.6, repeat: Infinity, ease: 'easeInOut', delay: 0.4 },
  },
}
const TIRED_EYE: Variants = {
  idle: { y: 0 },
  active: { y: 1, transition: { duration: 0.35 } },
}
const TIRED_MOUTH: Variants = {
  idle: { d: 'M 12 23 Q 18 21 24 23' },
  active: { d: 'M 11 25 Q 18 19 25 25' },
}
const OKAY_MOUTH: Variants = {
  idle: { d: 'M 13 21 Q 18 24 23 21' },
  active: { d: 'M 11 20 Q 18 29 25 20' },
}
const FLAME_WIGGLE: Variants = {
  idle: { scale: 1, rotate: 0 },
  active: {
    scale: [1, 1.06, 1],
    rotate: [-2, 3, -2],
    transition: { duration: 1.3, repeat: Infinity, ease: 'easeInOut' },
  },
}
const BOLT_PULSE: Variants = {
  idle: { scale: 1, filter: 'drop-shadow(0 0 0 rgba(255, 200, 0, 0))' },
  active: {
    scale: [1, 1.15, 1],
    filter: [
      'drop-shadow(0 0 0 rgba(255, 200, 0, 0))',
      'drop-shadow(0 0 6px rgba(255, 200, 0, 0.8))',
      'drop-shadow(0 0 0 rgba(255, 200, 0, 0))',
    ],
    transition: { duration: 1.2, repeat: Infinity, ease: 'easeInOut' },
  },
}

function EnergyIcon({ value }: { value: number }) {
  const common = {
    width: ICON_SIZE,
    height: ICON_SIZE,
    viewBox: '0 0 36 36',
    xmlns: 'http://www.w3.org/2000/svg',
  }

  if (value === 1) {
    // Sleepy face with floating Z's
    return (
      <svg {...common}>
        <circle cx="16" cy="19" r="12" fill={FACE_YELLOW} stroke={FACE_STROKE} strokeWidth="1.2" />
        <path d="M 10 17 Q 12 19 14 17" stroke={FACE_FEATURE} strokeWidth="1.6" strokeLinecap="round" fill="none" />
        <path d="M 18 17 Q 20 19 22 17" stroke={FACE_FEATURE} strokeWidth="1.6" strokeLinecap="round" fill="none" />
        <ellipse cx="16" cy="24" rx="1.8" ry="1.3" fill={FACE_FEATURE} opacity="0.75" />
        <motion.text x="24" y="10" fontSize="7" fontWeight="800" fill="#6E8EF0" variants={Z_FLOAT_A}>z</motion.text>
        <motion.text x="29" y="15" fontSize="5" fontWeight="800" fill="#6E8EF0" variants={Z_FLOAT_B}>z</motion.text>
      </svg>
    )
  }

  if (value === 2) {
    // Tired frown — eyes droop, mouth deepens on hover
    return (
      <svg {...common}>
        <circle cx="18" cy="18" r="12" fill={FACE_YELLOW} stroke={FACE_STROKE} strokeWidth="1.2" />
        <motion.circle cx="13" cy="16" r="1.5" fill={FACE_FEATURE} variants={TIRED_EYE} />
        <motion.circle cx="23" cy="16" r="1.5" fill={FACE_FEATURE} variants={TIRED_EYE} />
        <motion.path
          variants={TIRED_MOUTH}
          stroke={FACE_FEATURE}
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    )
  }

  if (value === 3) {
    // Smile that widens into a grin on hover
    return (
      <svg {...common}>
        <circle cx="18" cy="18" r="12" fill={FACE_YELLOW} stroke={FACE_STROKE} strokeWidth="1.2" />
        <circle cx="13" cy="15" r="1.5" fill={FACE_FEATURE} />
        <circle cx="23" cy="15" r="1.5" fill={FACE_FEATURE} />
        <motion.path
          variants={OKAY_MOUTH}
          stroke={FACE_FEATURE}
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    )
  }

  if (value === 4) {
    // Flame — internal warmth / strength. Flickers & tilts on hover.
    return (
      <motion.svg {...common} variants={FLAME_WIGGLE} style={{ transformOrigin: '18px 22px' }}>
        <path
          d="M 18 4 C 22 8 24 12 23 16 C 25 14 27 16 27 19 C 27 25 23 31 18 31 C 13 31 9 25 9 19 C 9 15 12 13 14 15 C 13 10 15 6 18 4 Z"
          fill="#FF7A3A"
          stroke="#B8431A"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
        <path
          d="M 18 14 C 20 16 21 19 20 22 C 19 23 17 23 16 22 C 15 20 16 17 18 14 Z"
          fill="#FFD34E"
        />
      </motion.svg>
    )
  }

  // 5 — Lightning bolt with a pulsing glow
  return (
    <motion.svg {...common} variants={BOLT_PULSE} style={{ transformOrigin: 'center' }}>
      <path
        d="M 21 3 L 9 20 L 16 20 L 13 33 L 27 15 L 19 15 Z"
        fill="#FFC83A"
        stroke="#B38208"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
    </motion.svg>
  )
}

const ENERGY_SCALE: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Exhausted' },
  { value: 2, label: 'Tired' },
  { value: 3, label: 'Okay' },
  { value: 4, label: 'Good' },
  { value: 5, label: 'Energized' },
]

const SLEEP_PRESETS = [4, 6, 7, 8, 9]
const SLEEP_MIN = 2
const SLEEP_MAX = 12
const SLEEP_STEP = 0.5

function Step3SleepEnergy({
  sleepHours,
  energyLevel,
  sleepPrefilled = false,
  energyPrefilled = false,
  prefillSource,
  onSleepChange,
  onEnergyChange,
}: {
  sleepHours: number
  energyLevel: number
  sleepPrefilled?: boolean
  energyPrefilled?: boolean
  prefillSource?: string | null
  onSleepChange: (v: number) => void
  onEnergyChange: (v: number) => void
}) {
  const clampSleep = (v: number) =>
    Math.min(SLEEP_MAX, Math.max(SLEEP_MIN, Math.round(v * 2) / 2))
  const dec = () => onSleepChange(clampSleep(sleepHours - SLEEP_STEP))
  const inc = () => onSleepChange(clampSleep(sleepHours + SLEEP_STEP))
  const sleepLabel =
    sleepHours < 5
      ? 'Not much'
      : sleepHours < 7
      ? 'A bit short'
      : sleepHours <= 9
      ? 'Solid rest'
      : 'Long sleep'

  return (
    <div className="checkin-step-content">
      <h3 className="checkin-title">Sleep &amp; energy</h3>

      <label className="form-label">
        Sleep last night
        {sleepPrefilled && <PrefillPill source={prefillSource || 'oura'} />}
      </label>
      <div className="stepper">
        <button
          type="button"
          className="stepper-btn"
          onClick={dec}
          disabled={sleepHours <= SLEEP_MIN}
          aria-label="Decrease sleep hours"
        >
          −
        </button>
        <div className="stepper-value" aria-live="polite">
          <span className="stepper-num">{sleepHours}</span>
          <span className="stepper-unit">hours</span>
        </div>
        <button
          type="button"
          className="stepper-btn"
          onClick={inc}
          disabled={sleepHours >= SLEEP_MAX}
          aria-label="Increase sleep hours"
        >
          +
        </button>
      </div>
      <div className="stepper-caption">{sleepLabel}</div>
      <div className="sleep-presets">
        {SLEEP_PRESETS.map((h) => (
          <button
            key={h}
            type="button"
            className={`sleep-preset${sleepHours === h ? ' selected' : ''}`}
            onClick={() => onSleepChange(h)}
          >
            {h}h
          </button>
        ))}
      </div>

      <label className="form-label" style={{ marginTop: '1.5rem' }}>
        Energy level
        {energyPrefilled && <PrefillPill source={prefillSource || 'oura'} />}
      </label>
      <div className="energy-scale" role="radiogroup" aria-label="Energy level">
        {ENERGY_SCALE.map((opt) => {
          const selected = energyLevel === opt.value
          return (
            <motion.button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={selected}
              className={`energy-option${selected ? ' selected' : ''}`}
              onClick={() => onEnergyChange(opt.value)}
              initial="idle"
              animate={selected ? 'active' : 'idle'}
              whileHover="active"
              whileFocus="active"
            >
              <span className="energy-emoji" aria-hidden>
                <EnergyIcon value={opt.value} />
              </span>
              <span className="energy-label">{opt.label}</span>
            </motion.button>
          )
        })}
      </div>
    </div>
  )
}

function FlowProductsStepper({
  value,
  onChange,
}: {
  value: number | null
  onChange: (n: number | null) => void
}) {
  const current = value ?? 0
  const dec = () => {
    const next = current - 1
    onChange(next <= 0 ? null : next)
  }
  const inc = () => onChange(Math.min(current + 1, 30))
  return (
    <div className="flow-products-stepper" role="group" aria-label="Pad or tampon count">
      <button
        type="button"
        className="flow-products-btn"
        onClick={dec}
        disabled={current === 0}
        aria-label="Decrease count"
      >
        −
      </button>
      <span className="flow-products-count" aria-live="polite">
        {value == null ? '—' : `${value}`}
      </span>
      <button
        type="button"
        className="flow-products-btn"
        onClick={inc}
        disabled={current >= 30}
        aria-label="Increase count"
      >
        +
      </button>
    </div>
  )
}

function Step4CycleSymptoms({
  cyclePhase,
  mucus,
  padsCount,
  tamponsCount,
  symptoms,
  quickSymptoms,
  otherSymptoms,
  expanded,
  onCycleChange,
  onMucusChange,
  onPadsChange,
  onTamponsChange,
  onSymptomToggle,
  onOtherChange,
  onToggleExpanded,
}: {
  cyclePhase: string | null
  mucus: string | null
  padsCount: number | null
  tamponsCount: number | null
  symptoms: string[]
  quickSymptoms: ReadonlyArray<readonly [string, string]>
  otherSymptoms: string[]
  expanded: boolean
  onCycleChange: (v: string) => void
  onMucusChange: (v: string) => void
  onPadsChange: (n: number | null) => void
  onTamponsChange: (n: number | null) => void
  onSymptomToggle: (v: string) => void
  onOtherChange: (next: string[]) => void
  onToggleExpanded: () => void
}) {
  const showFlowProducts = cyclePhase != null && BLEEDING_CYCLE_VALUES.has(cyclePhase)
  return (
    <div className="checkin-step-content">
      <h3 className="checkin-title">Cycle &amp; symptoms</h3>
      <label className="form-label">Cycle today</label>
      <ChipGroup
        options={CYCLE_OPTIONS}
        selected={cyclePhase ? [cyclePhase] : []}
        onToggle={onCycleChange}
      />
      {showFlowProducts && (
        <>
          <div className="flow-products-row">
            <label className="form-label" style={{ marginTop: '1rem' }}>
              How many pads today? <span className="form-hint">(optional)</span>
            </label>
            <FlowProductsStepper value={padsCount} onChange={onPadsChange} />
          </div>
          <div className="flow-products-row">
            <label className="form-label" style={{ marginTop: '1rem' }}>
              How many tampons today? <span className="form-hint">(optional)</span>
            </label>
            <FlowProductsStepper value={tamponsCount} onChange={onTamponsChange} />
          </div>
        </>
      )}
      <label className="form-label" style={{ marginTop: '1rem' }}>
        Cervical mucus <span className="form-hint">(optional)</span>
      </label>
      <ChipGroup
        options={MUCUS_OPTIONS}
        selected={mucus ? [mucus] : []}
        onToggle={onMucusChange}
      />
      <label className="form-label" style={{ marginTop: '1rem' }}>Symptoms today</label>
      {!expanded && (
        <ChipGroup options={quickSymptoms} selected={symptoms} onToggle={onSymptomToggle} multi />
      )}
      <button
        type="button"
        className={`btn-expand-symptoms${expanded ? ' expanded' : ''}`}
        onClick={onToggleExpanded}
      >
        {expanded ? '− Fewer symptoms' : '+ More symptoms'}
      </button>
      {expanded && (
        <div className="expanded-symptoms">
          {ALL_SYMPTOM_GROUPS.map((group) => (
            <div key={group.label}>
              <label className="form-label symptom-cat-label">{group.label}</label>
              <ChipGroup
                options={group.options}
                selected={symptoms}
                onToggle={onSymptomToggle}
                multi
              />
            </div>
          ))}
          <label className="form-label symptom-cat-label">Other symptom</label>
          <ChipGroup
            options={[['other', 'Other']]}
            selected={symptoms}
            onToggle={onSymptomToggle}
            multi
          />
          {symptoms.includes('other') && (() => {
            const entries = otherSymptoms.length > 0 ? otherSymptoms : ['']
            return (
              <div className="other-symptoms">
                <p className="form-hint" style={{ margin: '0.25rem 0 0.5rem' }}>
                  Name any symptom that isn't listed above — add a row for each one.
                </p>
                {entries.map((entry, i) => (
                  <div key={i} className="other-symptom-row">
                    <input
                      className="form-input"
                      placeholder="e.g. itchy skin, ringing ears"
                      value={entry}
                      onChange={(e) => {
                        const next = [...entries]
                        next[i] = e.target.value
                        onOtherChange(next)
                      }}
                      maxLength={200}
                    />
                    {entries.length > 1 && (
                      <button
                        type="button"
                        className="other-symptom-remove"
                        aria-label="Remove this symptom"
                        onClick={() => onOtherChange(entries.filter((_, j) => j !== i))}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className="other-symptom-add"
                  onClick={() => onOtherChange([...entries, ''])}
                >
                  + Add another
                </button>
              </div>
            )
          })()}
        </div>
      )}
    </div>
  )
}

function Step5Lifestyle({
  stress,
  food,
  alcohol,
  stressPrefilled = false,
  prefillSource,
  onStressChange,
  onFoodToggle,
  onAlcoholChange,
}: {
  stress: string | null
  food: string[]
  alcohol: string | null
  stressPrefilled?: boolean
  prefillSource?: string | null
  onStressChange: (v: string) => void
  onFoodToggle: (v: string) => void
  onAlcoholChange: (v: string) => void
}) {
  return (
    <div className="checkin-step-content">
      <h3 className="checkin-title">Lifestyle</h3>
      <p className="checkin-desc">These help us find your triggers</p>
      <label className="form-label">
        Stress level
        {stressPrefilled && <PrefillPill source={prefillSource || 'oura'} />}
      </label>
      <ChipGroup
        options={STRESS_OPTIONS}
        selected={stress ? [stress] : []}
        onToggle={onStressChange}
      />
      <label className="form-label" style={{ marginTop: '1rem' }}>
        Food today <span className="form-hint">(select any that apply)</span>
      </label>
      <ChipGroup options={FOOD_OPTIONS} selected={food} onToggle={onFoodToggle} multi />
      <label className="form-label" style={{ marginTop: '1rem' }}>Alcohol</label>
      <ChipGroup
        options={ALCOHOL_OPTIONS}
        selected={alcohol ? [alcohol] : []}
        onToggle={onAlcoholChange}
      />
    </div>
  )
}

function Step6Medications({
  meds,
  taken,
  onToggle,
}: {
  meds: Array<{ id: number; name: string; dosage?: string | null }>
  taken: Record<string, boolean>
  onToggle: (id: number) => void
}) {
  return (
    <div className="checkin-step-content">
      <h3 className="checkin-title">Medications</h3>
      <p className="checkin-desc">Check off the ones you took yesterday</p>
      <div className="checkin-meds-list">
        {meds.map((m) => {
          const isTaken = taken[String(m.id)] === true
          return (
            <button
              key={m.id}
              type="button"
              role="checkbox"
              aria-checked={isTaken}
              aria-label={`Mark ${m.name} as taken yesterday`}
              className={`checkin-med-row checkin-med-toggle${isTaken ? ' checked' : ''}`}
              onClick={() => onToggle(m.id)}
            >
              <span className={`checkin-med-check${isTaken ? ' checked' : ''}`} aria-hidden>
                {isTaken && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </span>
              <span className="checkin-med-info">
                <span className="checkin-med-name">{m.name}</span>
                {m.dosage && <span className="checkin-med-dosage">{m.dosage}</span>}
              </span>
              <span className="checkin-med-state">{isTaken ? 'Taken' : 'Not taken'}</span>
            </button>
          )
        })}
      </div>
      <p className="checkin-meds-hint">Anything left unchecked is logged as not taken yesterday.</p>
    </div>
  )
}

// -------- Shared chip group --------

interface ChipGroupProps {
  options: ReadonlyArray<readonly [string, string]>
  selected: string[]
  onToggle: (v: string) => void
  multi?: boolean
}

function ChipGroup({ options, selected, onToggle, multi }: ChipGroupProps) {
  return (
    <div className={`chip-group${multi ? '' : ' chip-group-single'}`}>
      {options.map(([value, label]) => {
        const isSelected = selected.includes(value)
        return (
          <button
            key={value}
            type="button"
            className={`chip${isSelected ? ' selected' : ''}`}
            onClick={() => onToggle(value)}
            aria-pressed={isSelected}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
